import { useState, useEffect, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { History, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { logDev } from "@/lib/ux";
import { fmtDateTime } from "@/lib/format";

// Read-only edit history for a note (Admin/Manager only — gated by the caller).
export default function NoteHistoryModal({ open, onOpenChange, leadId, note }) {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!note?.id) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/leads/${leadId}/notes/${note.id}/history`);
      setHistory(data.data);
    } catch (e) { logDev("Failed to load note history", e); }
    finally { setLoading(false); }
  }, [leadId, note]);

  useEffect(() => { if (open) load(); }, [open, load]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="border-sky bg-white sm:max-w-lg" data-testid="note-history-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-sora text-navy"><History className="h-5 w-5 text-loop" />Note History</DialogTitle>
        </DialogHeader>
        <DialogDescription className="text-xs text-ink/60">Previous versions of this note, newest first. Current text is shown at the top for reference.</DialogDescription>
        <div className="max-h-[60vh] space-y-3 overflow-y-auto">
          {/* Current version */}
          <div className="rounded-xl border border-loop/30 bg-loop/[0.04] p-3">
            <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-loop">Current</p>
            <p className="whitespace-pre-wrap text-sm text-ink">{note?.content}</p>
            <p className="mt-1.5 text-[11px] text-ink/45">{note?.updated_by_name ? `Last edited by ${note.updated_by_name} · ${fmtDateTime(note.updated_at)}` : `Created by ${note?.created_by_name || "—"} · ${fmtDateTime(note?.created_at)}`}</p>
          </div>
          {loading ? (
            <div className="flex items-center justify-center gap-2 py-6 text-sm text-ink/50"><Loader2 className="h-4 w-4 animate-spin" />Loading history…</div>
          ) : history.length === 0 ? (
            <p className="py-4 text-center text-sm text-ink/50">No earlier versions — this note hasn't been edited.</p>
          ) : (
            [...history].reverse().map((h, i) => (
              <div key={i} data-testid={`note-history-${i}`} className="rounded-xl border border-sky/70 bg-white p-3">
                <p className="whitespace-pre-wrap text-sm text-ink/80">{h.content}</p>
                <p className="mt-1.5 text-[11px] text-ink/45">Edited by {h.edited_by_name || "—"} · {fmtDateTime(h.edited_at)}{h.reason ? ` · Reason: ${h.reason}` : ""}</p>
              </div>
            ))
          )}
        </div>
        <div className="flex justify-end">
          <button onClick={() => onOpenChange(false)} className="rounded-lg border border-sky px-4 py-2 text-sm font-medium text-ink/70 hover:bg-mist">Close</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
